import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShieldCheck, AlertTriangle, MessageCircle, ClipboardCheck, PoundSterling } from "lucide-react";
import { api, formatApiError as formatErr } from "../lib/api";
import { SECTION_A_FIELDS, SECTION_B_FIELDS } from "./ArrangementRecord";

/* ────────────────────────────────────────────────────────────────────────
   Family Care Office — carer's own, read-only view of the Care
   Arrangement connected to their Room. Access is scoped strictly via
   room_id + the signed-in carer_email on the backend — never via
   placement_id.
   /care-discussion-room/:roomId/my-arrangement
   ──────────────────────────────────────────────────────────────────────── */

function ReadOnlyField({ field, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-ink-soft">{field.label}</div>
      <p className="mt-1 text-sm text-ink whitespace-pre-wrap" data-testid={`carer-arrangement-field-${field.key}`}>{value}</p>
    </div>
  );
}

function ReadOnlySection({ eyebrow, title, icon: Icon, fields, values }) {
  const anyValue = fields.some((f) => values[f.key]);
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
      <div className="flex items-start gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center shrink-0" aria-hidden="true">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div>
          <div className="overline text-purple">{eyebrow}</div>
          <h2 className="font-display text-xl font-semibold">{title}</h2>
        </div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-5">
        {anyValue ? fields.map((f) => <ReadOnlyField key={f.key} field={f} value={values[f.key]} />)
          : <p className="text-sm text-ink-soft sm:col-span-2">Nothing recorded here yet.</p>}
      </div>
    </section>
  );
}

export default function CarerArrangementView() {
  const { roomId } = useParams();
  const [loading, setLoading] = useState(true);
  const [arrangement, setArrangement] = useState(null);
  const [error, setError] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);

  const load = () => {
    api.get(`/api/care-arrangements/by-room/${roomId}/carer-view`)
      .then((r) => setArrangement(r.data))
      .catch((err) => setError(err?.response?.status === 404
        ? "Your family hasn't set up a Care Arrangement for this Room yet."
        : formatErr(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  const acknowledge = async () => {
    if (acknowledging) return;
    setAcknowledging(true);
    try {
      const { data } = await api.post(`/api/care-arrangements/by-room/${roomId}/acknowledge`);
      setArrangement(data);
      toast.success("Acknowledgement recorded");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setAcknowledging(false);
    }
  };

  if (loading) {
    return (
      <div className="container-jcc py-24 text-center text-ink-muted" data-testid="carer-arrangement-loading">
        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-jcc py-24 max-w-lg text-center" data-testid="carer-arrangement-error">
        <p className="text-ink-muted">{error}</p>
        <Link to={`/care-discussion-room/${roomId}`} className="btn-outline mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Back to Room
        </Link>
      </div>
    );
  }

  const isStale = arrangement.acknowledged_by_carer_at && arrangement.acknowledged_version !== arrangement.version;
  const neverAcknowledged = !arrangement.acknowledged_by_carer_at;

  return (
    <div className="bg-[#fafaf7] min-h-screen" data-testid="page-carer-arrangement">
      <div className="container-jcc py-10 md:py-14 max-w-4xl">
        <div className="mb-6">
          <Link to={`/care-discussion-room/${roomId}`} className="btn-outline !px-4 !py-2 text-sm" data-testid="carer-arrangement-back-to-room">
            <ArrowLeft className="w-4 h-4" /> Back to Care Partnership Room
          </Link>
        </div>

        <div className="mb-8">
          <div className="overline text-purple mb-3">Family Care Office</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">Your Care Arrangement</h1>
          <p className="mt-3 text-ink-muted leading-relaxed max-w-2xl">
            A read-only record of what you and this family have agreed. Only they can edit it.
          </p>
        </div>

        {(neverAcknowledged || isStale) && (
          <div
            className={`mb-6 rounded-2xl border p-5 flex items-start gap-3 ${isStale ? "border-amber-300 bg-amber-50" : "border-purple/20 bg-purple/5"}`}
            data-testid={isStale ? "carer-arrangement-stale-banner" : "carer-arrangement-unacknowledged-banner"}
          >
            <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isStale ? "text-amber-700" : "text-purple"}`} />
            <div className="flex-1">
              <p className="font-medium text-ink">
                {isStale ? "This Arrangement has been updated since you last acknowledged it" : "Please review and acknowledge this Arrangement"}
              </p>
              <p className="text-sm text-ink-muted mt-1">
                {isStale
                  ? "Check what's changed below, then acknowledge it again once you're happy."
                  : "Once you're happy this reflects what you agreed, acknowledge it below."}
              </p>
            </div>
            <button type="button" onClick={acknowledge} disabled={acknowledging} className="btn-purple text-sm whitespace-nowrap" data-testid="carer-arrangement-acknowledge-btn">
              {acknowledging ? "Saving…" : "Acknowledge this Arrangement"}
            </button>
          </div>
        )}

        {!neverAcknowledged && !isStale && (
          <div className="mb-6 rounded-2xl border border-teal/20 bg-teal-50 p-5 flex items-start gap-3" data-testid="carer-arrangement-acknowledged-banner">
            <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-teal" />
            <p className="text-sm text-ink">
              You acknowledged this Arrangement on{" "}
              {new Date(arrangement.acknowledged_by_carer_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.
            </p>
          </div>
        )}

        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3">
          <MessageCircle className="w-5 h-5 mt-0.5 shrink-0 text-ink-soft" />
          <div>
            <p className="text-sm text-ink font-medium">Need something changed?</p>
            <p className="text-sm text-ink-muted mt-1">
              Message the family in your Room — the same place you discuss everything else. There's no separate change-request process.
            </p>
            <Link to={`/care-discussion-room/${roomId}`} className="text-sm text-purple font-medium hover:underline mt-2 inline-block" data-testid="carer-arrangement-message-family-link">
              Go to Discussion Log
            </Link>
          </div>
        </div>

        {arrangement.next_review_date && (
          <p className="mb-6 text-sm text-ink-muted" data-testid="carer-arrangement-review-date">
            Next review date: <strong>{arrangement.next_review_date}</strong>
          </p>
        )}

        <div className="space-y-6">
          <ReadOnlySection eyebrow="Section A" title="The arrangement" icon={ClipboardCheck} fields={SECTION_A_FIELDS} values={arrangement} />
          <ReadOnlySection eyebrow="Section B" title="Money & practical working terms" icon={PoundSterling} fields={SECTION_B_FIELDS} values={arrangement} />
        </div>
      </div>
    </div>
  );
}
